import { goSolidApi } from "@/composables/useGoSolidApi";
import type { Ref } from "vue";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import {
  getPricingLoadingMessages,
  runPricingLoadingSequence,
} from "../common/pricingLoadingUtils";
import type {
  BulkBuyPricingRequest,
  BulkBuyPricingResponse,
  CompanyRow,
  GlobalPurchaseSettings,
  PricingState,
  UseBulkBuyPricingReturn,
} from "./bulkBuyTypes";
import { isBuyRowComplete } from "./bulkBuyTypes";

export function useBulkBuyPricing(
  companies: Ref<CompanyRow[]>,
  globalSettings: Ref<GlobalPurchaseSettings>
): UseBulkBuyPricingReturn {
  const { t } = useI18n();
  const isLoadingAny = ref(false);

  const canSubmitOrder = computed(() => {
    if (companies.value.length === 0) return false;

    return companies.value.every((row) => {
      const state = row.pricing_state;
      return (
        state &&
        (state.status === "success" || state.status === "agent_reach_out")
      );
    });
  });

  const updatePricingState = (rowIndex: number, state: PricingState) => {
    const company = companies.value[rowIndex];
    if (company) {
      company.pricing_state = state;
    }
  };

  const clearPricingState = (rowIndex: number) => {
    updatePricingState(rowIndex, { status: "idle" });
  };

  const fetchPricing = async (rowIndex: number) => {
    const company = companies.value[rowIndex];
    if (!company) return;

    try {
      isLoadingAny.value = true;
      const loadingMessages = getPricingLoadingMessages((key) => t(key));

      const request: BulkBuyPricingRequest = {
        companies: [
          {
            volume: company.volume,
            country: company.country,
          },
        ],
        global_settings: globalSettings.value,
      };

      const response = await runPricingLoadingSequence(
        (state) => updatePricingState(rowIndex, state),
        loadingMessages,
        goSolidApi.post<BulkBuyPricingResponse>(
          "/api/eac/buy-and-cancel/pricing",
          request
        )
      );

      const companyPricing = response.data.company_pricing[0];

      if (
        companyPricing.status === "PRICE_FOUND" &&
        companyPricing.total_price !== undefined
      ) {
        updatePricingState(rowIndex, {
          status: "success",
          price: Number(companyPricing.total_price),
          price_date: companyPricing.price_date ?? undefined,
          pricing_request_id: response.data.request_id ?? undefined,
          base_price: Number(companyPricing.base_price ?? 0),
          brokerage_fee: Number(companyPricing.brokerage_fee ?? 0),
          premiums: Object.fromEntries(
            Object.entries(companyPricing.premiums || {}).map(([k, v]) => [
              k,
              Number(v),
            ])
          ),
          cancellation_fee: Number(companyPricing.cancellation_fee ?? 0),
          minimum_cancellation_fee: Number(
            companyPricing.minimum_cancellation_fee ?? 0
          ),
          go_cost_per_mwh: Number(companyPricing.go_cost_per_mwh ?? 0),
          mwh_cost: Number(companyPricing.mwh_cost ?? 0),
          cancellation_cost: Number(companyPricing.cancellation_cost ?? 0),
          cancellation_supported: companyPricing.cancellation_supported ?? true,
        });
      } else {
        updatePricingState(rowIndex, {
          status: "agent_reach_out",
          pricing_request_id: response.data.request_id ?? undefined,
        });
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      updatePricingState(rowIndex, {
        status: "error",
        message,
      });
    } finally {
      const stillLoading = companies.value.some(
        (row) => row.pricing_state.status === "loading"
      );
      if (!stillLoading) {
        isLoadingAny.value = false;
      }
    }
  };

  const fetchAllPricing = async () => {
    isLoadingAny.value = true;

    const promises = companies.value
      .map((row, index) => ({ row, index }))
      .filter(({ row }) => isBuyRowComplete(row))
      .filter(({ row }) => {
        const state = row.pricing_state;
        return !state || state.status === "idle" || state.status === "error";
      })
      .map(({ index }) => fetchPricing(index));

    await Promise.allSettled(promises);

    isLoadingAny.value = false;
  };

  return {
    isLoadingAny,
    fetchPricing,
    clearPricingState,
    fetchAllPricing,
    canSubmitOrder,
  };
}
